import * as React from 'react';
import moment from 'moment';
import { Tournament } from 'Src/models/tournament';
import { isIndividualTournament, isMiniTeamTournament, isTeamTournament } from 'Src/helpers/tournament/tournament';
import {
  PageHelpAction,
  PageHelpDefinition,
  PageHelpObservation,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';
import { CompetitionSection } from './TournamentCompetition';
import { EntriesSection } from './TournamentEntries';
import { SettingsSection } from './TournamentSettings';

export type UnionTournamentManagementTab =
  | 'overview'
  | 'entries'
  | 'competition'
  | 'communications'
  | 'publishing'
  | 'settings';

interface Props {
  tournament: Tournament;
  activeTab: UnionTournamentManagementTab;
  subsection?: string;
  confirmationSummary: { total: number; pending: number; rejected: number };
  onEdit: () => void;
  onReports: () => void;
  onNotifications: () => void;
  onTab: (tab: UnionTournamentManagementTab) => void;
  onEntriesSection: (section: EntriesSection) => void;
  onCompetitionSection: (section: CompetitionSection) => void;
  onSettingsSection: (section: SettingsSection) => void;
  onOpenEvents: () => void;
}

const TAB_LABELS = {
  overview: 'Overview',
  entries: 'Entries',
  competition: 'Competition',
  communications: 'Communications',
  publishing: 'Publishing',
  settings: 'Settings'
};

const ENTRY_LABELS = {
  rules: 'Entry rules',
  eligible: 'Eligible schools',
  participants: 'Participants',
  athleteSchools: 'Athletes’ schools',
  schoolAdmins: 'School admins',
  waiting: 'Waiting list',
  requests: 'Confirmation requests',
  log: 'Enrollment log'
};

const COMPETITION_LABELS = {
  teams: 'Teams',
  groups: 'Groups',
  pitches: 'Pitches',
  fixtures: 'Fixtures',
  events: 'Events'
};

const findEntrySection = (value?: string): EntriesSection =>
  Object.prototype.hasOwnProperty.call(ENTRY_LABELS, value || '') ? (value as EntriesSection) : 'rules';

const findCompetitionSection = (value: string | undefined, isTeam: boolean): CompetitionSection => {
  const fallback: CompetitionSection = 'teams';
  if (!Object.prototype.hasOwnProperty.call(COMPETITION_LABELS, value || '')) return fallback;
  if (isTeam && value === 'events') return fallback;
  if (!isTeam && (value === 'groups' || value === 'pitches' || value === 'fixtures')) return fallback;
  return value as CompetitionSection;
};

export const UnionTournamentManagementPageHelp: React.FunctionComponent<Props> = props => {
  const registrationRef = React.useRef<PageHelpRegistration | null>(null);
  const tournament = props.tournament;
  const isPast = moment(tournament.endTime || tournament.startTime).isBefore(moment());
  const isTeam = isTeamTournament(tournament) || isMiniTeamTournament(tournament);
  const isIndividual = isIndividualTournament(tournament);
  const entrySection = findEntrySection(props.subsection);
  const competitionSection = findCompetitionSection(props.subsection, isTeam);
  const activeView =
    props.activeTab === 'entries'
      ? `${TAB_LABELS.entries} · ${ENTRY_LABELS[entrySection]}`
      : props.activeTab === 'competition'
      ? `${TAB_LABELS.competition} · ${COMPETITION_LABELS[competitionSection]}`
      : props.activeTab === 'settings'
      ? `${TAB_LABELS.settings} · ${props.subsection === 'admins' ? 'Admins' : 'Details'}`
      : TAB_LABELS[props.activeTab];

  const hasBookingOpeningDate = Boolean(tournament.entriesOpen) && moment(tournament.entriesOpen).isValid();
  const hasBookingClosingDate =
    Boolean(tournament.deadlineForEntries) && moment(tournament.deadlineForEntries).isValid();
  const bookingDatesInOrder =
    hasBookingOpeningDate &&
    hasBookingClosingDate &&
    !moment(tournament.entriesOpen).isAfter(moment(tournament.deadlineForEntries));
  const hasValidBookingWindow = hasBookingOpeningDate && hasBookingClosingDate && bookingDatesInOrder;
  const isBookingOpenNow =
    hasValidBookingWindow &&
    moment().isSameOrAfter(moment(tournament.entriesOpen)) &&
    moment().isSameOrBefore(moment(tournament.deadlineForEntries));
  const isPublishedOnUnionWebsite = Boolean(tournament.isPublishOnTheWebsite);
  const autoEnrollmentSettings = tournament.autoEnrollmentTeamsSettings || [];
  const hasAutoEnrollmentCapacityRules = autoEnrollmentSettings.length > 0;
  const bookingWindowText = hasValidBookingWindow
    ? `${moment(tournament.entriesOpen).format('DD MMM YYYY [at] HH:mm')} to ${moment(
        tournament.deadlineForEntries
      ).format('DD MMM YYYY [at] HH:mm')}`
    : '';
  const hasBookingSetupIssue = !hasBookingOpeningDate || !hasBookingClosingDate || !bookingDatesInOrder;

  const actions: PageHelpAction[] = [
    {
      id: 'union-tournament-edit',
      label: 'Edit tournament details',
      description:
        hasBookingSetupIssue && !isPast
          ? 'Open Schedule & venue and set a valid Entries open date and Deadline for entries so schools can book online.'
          : isPast
          ? 'Correct the completed tournament’s dates, venue, rules or other core details. Past tournaments are not locked.'
          : 'Update the tournament’s core details and entry configuration.',
      kind: hasBookingSetupIssue && !isPast ? 'primary' : undefined,
      anchorId: 'union-tournament-header-actions',
      run: props.onEdit
    },
    {
      id: 'union-tournament-notifications',
      label: 'Open notification history',
      description: 'Review what was sent, when it was sent, who received it and the delivery status.',
      anchorId: 'union-tournament-header-actions',
      run: props.onNotifications
    },
    {
      id: 'union-tournament-reports',
      label: 'Open tournament reports',
      description: 'Choose a report using the tournament data currently stored in the system.',
      anchorId: 'union-tournament-header-actions',
      run: props.onReports
    }
  ];

  if (props.activeTab === 'overview') {
    actions.push(
      {
        id: 'union-tournament-open-entries',
        label: 'Manage entries',
        description: 'Open entry rules, eligible schools, participants, waiting list and confirmations.',
        kind: 'primary',
        anchorId: 'union-tournament-tabs',
        run: () => props.onTab('entries')
      },
      {
        id: 'union-tournament-open-competition',
        label: isPast ? 'Manage past competition' : 'Set up competition',
        description: isPast
          ? 'Continue editing teams, participants, fixtures or events and results for this completed tournament.'
          : 'Manage teams and build the tournament’s fixtures or individual event programme.',
        anchorId: 'union-tournament-tabs',
        run: () => props.onTab('competition')
      }
    );
  } else if (props.activeTab === 'entries') {
    const nextEntryAction: { section: EntriesSection; label: string; description: string } =
      entrySection === 'rules'
        ? {
            section: 'eligible',
            label: 'Open eligible schools',
            description: 'Choose the schools that may enter, then send invitations when ready.'
          }
        : entrySection === 'eligible'
        ? {
            section: 'participants',
            label: 'Open participants',
            description: 'Review accepted schools and manage their tournament places.'
          }
        : entrySection === 'participants'
        ? {
            section: 'requests',
            label: 'Open confirmation requests',
            description: 'Request and review final participation confirmations.'
          }
        : {
            section: 'log',
            label: 'Open enrollment log',
            description: 'Review the audit trail for entry and waiting-list changes.'
          };
    actions.push({
      id: 'union-tournament-next-entry-area',
      label: nextEntryAction.label,
      description: nextEntryAction.description,
      kind: 'primary',
      anchorId: 'union-tournament-content',
      run: () => props.onEntriesSection(nextEntryAction.section)
    });
  } else if (props.activeTab === 'competition') {
    if (competitionSection !== 'teams') {
      actions.push({
        id: 'union-tournament-open-teams',
        label: 'Open tournament teams',
        description: 'Review school teams and their members. This remains available after the tournament has finished.',
        anchorId: 'union-tournament-content',
        run: () => props.onCompetitionSection('teams')
      });
    }
    actions.push({
      id: 'union-tournament-open-events',
      label: isTeam ? 'Manage fixtures & results' : 'Manage events & results',
      description: isPast
        ? 'Open the completed competition programme to add or correct allocations, scores, placings and results.'
        : isTeam
        ? 'Build fixtures, assign teams and pitches, then record scores and results.'
        : 'Build events, allocate participants and record scores, times, distances or placings.',
      kind: 'primary',
      anchorId: 'union-tournament-content',
      run: props.onOpenEvents
    });
  } else if (props.activeTab === 'publishing') {
    actions.push({
      id: 'union-tournament-review-competition-before-publishing',
      label: 'Review competition data',
      description:
        'Check teams, events and results before publishing or updating public-facing tournament information.',
      anchorId: 'union-tournament-tabs',
      run: () => props.onTab('competition')
    });
  } else if (props.activeTab === 'settings') {
    actions.push({
      id: 'union-tournament-switch-settings',
      label: props.subsection === 'admins' ? 'Open tournament details' : 'Manage tournament admins',
      description:
        props.subsection === 'admins'
          ? 'Return to the tournament settings form.'
          : 'Choose the union users allowed to administer this tournament.',
      anchorId: 'union-tournament-content',
      run: () => props.onSettingsSection(props.subsection === 'admins' ? 'details' : 'admins')
    });
  }

  const priorityActions: PageHelpAction[] = [];
  if (!isPast && hasValidBookingWindow && !tournament.isAutoEnrollmentTeams) {
    priorityActions.push({
      id: 'union-tournament-fix-auto-enrolment',
      label: 'Review auto-enrolment rules',
      description:
        'Open Entries → Entry rules, choose Edit rules, enable auto-enrolment and set capacity for the tournament’s age and gender groups.',
      kind: 'primary',
      anchorId: 'union-tournament-tabs',
      run: () => props.onEntriesSection('rules')
    });
  } else if (!isPast && tournament.isAutoEnrollmentTeams && !hasAutoEnrollmentCapacityRules) {
    priorityActions.push({
      id: 'union-tournament-add-auto-enrolment-capacity',
      label: 'Set auto-enrolment capacity',
      description:
        'Open Entries → Entry rules, choose Edit rules and set capacity for the tournament’s age and gender groups.',
      kind: 'primary',
      anchorId: 'union-tournament-tabs',
      run: () => props.onEntriesSection('rules')
    });
  }
  if (!isPast && !isPublishedOnUnionWebsite) {
    priorityActions.push(
      {
        id: 'union-tournament-fix-website-visibility',
        label: 'Review website visibility',
        description:
          'Open Publishing, choose Edit public site and enable Publish tournament on union website if schools should discover it there.',
        kind: 'primary',
        anchorId: 'union-tournament-tabs',
        run: () => props.onTab('publishing')
      },
      {
        id: 'union-tournament-invite-schools-directly',
        label: 'Invite schools directly',
        description:
          'Keep the tournament hidden, but open Eligible schools to select the schools that should receive a direct invitation.',
        anchorId: 'union-tournament-content',
        run: () => props.onEntriesSection('eligible')
      }
    );
  }
  actions.unshift(...priorityActions);

  const observations: PageHelpObservation[] = [
    {
      id: 'tournament-time-state',
      tone: isPast ? 'positive' : 'neutral',
      title: isPast ? 'Completed tournament · management stays open' : 'Upcoming tournament',
      text: isPast
        ? 'The date has passed, but this tournament is not read-only. Entrants, teams, participants, events, allocations, scores, placings, results, publishing and settings can still be corrected.'
        : 'Use the management tabs to move from entry setup through competition delivery and publishing.'
    }
  ];

  if (!isPast && (!hasBookingOpeningDate || !hasBookingClosingDate)) {
    const missingDates = [
      !hasBookingOpeningDate ? 'Entries open' : '',
      !hasBookingClosingDate ? 'Deadline for entries' : ''
    ].filter(Boolean);
    observations.push({
      id: 'booking-window-missing',
      tone: 'danger',
      title:
        missingDates.length === 2 ? 'Online booking window is not configured' : 'Online booking window is incomplete',
      text: `${missingDates.join(' and ')} ${
        missingDates.length === 1 ? 'is' : 'are'
      } missing. Until both dates are set, schools cannot submit online tournament entries. Open Edit tournament details → Schedule & venue and set the complete booking interval.`
    });
  } else if (!isPast && !bookingDatesInOrder) {
    observations.push({
      id: 'booking-window-invalid',
      tone: 'danger',
      title: 'Booking dates are in the wrong order',
      text:
        'Entries open is later than Deadline for entries, so online booking cannot work. Open Edit tournament details → Schedule & venue and correct the interval.'
    });
  }

  if (!isPast && hasValidBookingWindow && !tournament.isAutoEnrollmentTeams) {
    observations.push({
      id: 'auto-enrolment-disabled',
      tone: 'warning',
      title: 'Booking is configured, but auto-enrolment is off',
      text: `The booking window is ${bookingWindowText}, but a school’s booking will not add it or its teams to the tournament automatically. Open Entries → Entry rules → Edit rules, enable auto-enrolment and set capacity. If manual enrolment is intentional, leave it off and manage accepted entrants in Participants.`
    });
  }

  if (!isPast && tournament.isAutoEnrollmentTeams && !hasAutoEnrollmentCapacityRules) {
    observations.push({
      id: 'auto-enrolment-capacity-missing',
      tone: 'danger',
      title: 'Auto-enrolment is on, but capacity is not set',
      text:
        'Schools cannot be auto-enrolled reliably until capacity rules exist for the tournament’s age and gender groups. Open Entries → Entry rules → Edit rules and configure the available places.'
    });
  }

  if (!isPast && !isPublishedOnUnionWebsite) {
    observations.push({
      id: 'tournament-hidden-from-union-website',
      tone: 'warning',
      title: 'Tournament is hidden from the union website',
      text: `Schools cannot discover this tournament or start booking from the union website listing. To make it visible, open Publishing → Edit public site and enable Publish tournament on union website. If it should stay hidden, send direct invitations from Entries → Eligible schools${
        hasValidBookingWindow ? '' : ' after configuring the booking window'
      }.`
    });
  }

  if (!isPast && isBookingOpenNow && tournament.isReadOnly) {
    observations.push({
      id: 'schools-read-only-during-booking',
      tone: 'danger',
      title: 'Booking dates are open, but schools are read-only',
      text:
        'Schools cannot complete their entry while Schools read-only is enabled. Open Entries → Entry rules → Edit rules and switch off read-only when you are ready to accept bookings.'
    });
  }

  if (props.confirmationSummary.pending > 0) {
    observations.push({
      id: 'pending-confirmations',
      tone: 'warning',
      title: `${props.confirmationSummary.pending} confirmation ${
        props.confirmationSummary.pending === 1 ? 'reply' : 'replies'
      } pending`,
      text: isPast
        ? 'The response record is retained. Review it before correcting the final participant and result data.'
        : 'Open Communications → Confirmation requests to review schools still awaiting a final response.'
    });
  }

  if (props.confirmationSummary.rejected > 0) {
    observations.push({
      id: 'rejected-confirmations',
      tone: 'warning',
      title: `${props.confirmationSummary.rejected} rejected`,
      text: 'Review rejected confirmations before relying on the final participant, team or fixture lists.'
    });
  }

  const definition: PageHelpDefinition = {
    screenId: `union.tournaments.manage.${props.activeTab}${
      props.activeTab === 'entries' || props.activeTab === 'competition' || props.activeTab === 'settings'
        ? `.${props.subsection ||
            (props.activeTab === 'entries' ? 'rules' : props.activeTab === 'competition' ? 'teams' : 'details')}`
        : ''
    }`,
    title: tournament.name,
    subtitle: `${activeView}. Squady checks booking, enrolment and visibility settings and explains what schools can do now.`,
    entity: { singular: 'tournament', plural: 'tournaments' },
    state: { activeView },
    observations,
    actions,
    anchors: [
      {
        id: 'union-tournament-header',
        label: 'Tournament summary',
        description:
          'The header keeps the tournament’s sport, type, date, venue, audience and booking state visible in every management area.'
      },
      {
        id: 'union-tournament-header-actions',
        label: 'Tournament-wide actions',
        description:
          'Reports, notification history and Edit tournament apply to the whole tournament and remain available for completed tournaments.'
      },
      {
        id: 'union-tournament-confirmations',
        label: 'Confirmation status',
        description:
          'These chips show sent, pending and rejected confirmation requests. Select one to open the request list.'
      },
      {
        id: 'union-tournament-tabs',
        label: 'Management sections',
        description:
          'Overview summarises readiness; Entries manages schools and responses; Competition manages teams, events and results; Publishing controls public output; Settings controls details and admins.'
      },
      {
        id: 'union-tournament-content',
        label: activeView,
        selector: '.utmOverview, .utmEntries, .utmCompetition, .utmPublishing, .utmSettings',
        description:
          props.activeTab === 'competition'
            ? isPast
              ? 'This completed tournament remains editable here, including teams, event allocations, fixtures, scores, placings and results.'
              : 'Build and run the competition here, including teams, groups or events, pitches, fixtures and results.'
            : props.activeTab === 'entries'
            ? 'Manage entry rules, eligible and participating schools, the waiting list, confirmations and the audit log.'
            : props.activeTab === 'publishing'
            ? 'Control what appears on the public tournament site and bigscreen without losing the underlying competition data.'
            : props.activeTab === 'settings'
            ? 'Update tournament configuration and administrator access. Unsaved changes are protected when you navigate away.'
            : 'Use the live metrics and checklist to move to the tournament area that needs attention.'
      }
    ],
    sections: [
      {
        id: 'online-entry-readiness',
        title: 'How online tournament entry works',
        items: [
          'Entries open and Deadline for entries define when schools are allowed to submit a booking.',
          'Auto-enrolment controls whether a valid booking adds the school or its teams automatically; capacity and waiting-list rules are configured alongside it.',
          'Publish tournament on union website controls whether schools can discover the tournament in the public listing.',
          'A hidden tournament can still be offered to selected schools by sending direct invitations from Entries → Eligible schools.'
        ],
        anchorIds: ['union-tournament-tabs', 'union-tournament-content']
      },
      {
        id: 'management-map',
        title: 'How the management hub is organised',
        items: [
          'Overview shows entry progress, current capacity, outstanding confirmations and setup readiness.',
          'Entries covers the journey from eligible schools and invites to participants and the waiting list.',
          isTeam
            ? 'Competition covers teams, groups, pitches, fixtures, scores and results.'
            : 'Competition covers the tournament participant team, individual events, allocations, performances and placings.',
          'Publishing controls public output; Settings controls tournament details and admin access.'
        ],
        anchorIds: ['union-tournament-tabs', 'union-tournament-content']
      },
      ...(isPast
        ? [
            {
              id: 'completed-tournament-work',
              title: 'What can still be changed after the tournament',
              items: [
                'Correct eligible, participating or waiting-list schools when the recorded entry history needs fixing.',
                'Add or amend tournament teams and participant membership.',
                isIndividual
                  ? 'Correct event allocations, performances, points and placings for individual participants.'
                  : 'Correct groups, pitches, fixtures, scores, points, placings and final results.',
                'Update public results and settings after verifying the corrected competition data.'
              ],
              anchorIds: ['union-tournament-content']
            }
          ]
        : []),
      {
        id: 'safe-actions',
        title: 'Actions and safeguards',
        items: [
          'Squady can open safe management areas but does not run deletion, removal or bulk state changes for you.',
          'Destructive actions keep their confirmation step.',
          'Unsaved settings changes show a discard warning before navigation.'
        ],
        anchorIds: ['union-tournament-header-actions', 'union-tournament-content']
      }
    ]
  };

  React.useEffect(() => {
    registrationRef.current = registerPageHelpDefinition(definition);
    return () => {
      if (registrationRef.current) registrationRef.current.unregister();
      registrationRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (registrationRef.current) registrationRef.current.update(definition);
  }, [
    tournament.id,
    tournament.name,
    tournament.startTime,
    tournament.endTime,
    tournament.entriesOpen,
    tournament.deadlineForEntries,
    tournament.isAutoEnrollmentTeams,
    hasAutoEnrollmentCapacityRules,
    tournament.isReadOnly,
    tournament.isPublishOnTheWebsite,
    props.activeTab,
    props.subsection,
    props.confirmationSummary.total,
    props.confirmationSummary.pending,
    props.confirmationSummary.rejected
  ]);

  return null;
};
